"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonLoading,
  IonToast,
  useIonViewWillEnter,
} from "@ionic/react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  IconButton,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Container,
  Stack,
  Autocomplete,
  Divider,
} from "@mui/material"
import {
  Add,
  Delete,
  ExpandMore,
  Inventory,
  ShoppingCart,
  Cancel,
  CheckCircle,
  Pending,
  Warning,
  Refresh,
  Receipt,
  Event,
  Person,
  Phone,
  Badge,
  Storefront,
  TrendingUp,
  TrendingDown,
  Balance,
  LocalOffer,
  AttachMoney,
  MonetizationOn,
  ShowChart,
} from "@mui/icons-material"
import { styled } from "@mui/material/styles"
import { getBarInventoryByBusiness, getProducts } from "../../../api/products/product"
import { getStockSalesReport, getStockSaleDetails } from "../../../api/reports/reports"
import { getProductsByBranch, getStocksTransfer, getTlocations_production_centre } from "../../../api/stock-transfer/stock-transfer"
import { getVendors } from "../../../api/vendor/vendor"

export interface StockItem {
  id: string;
  name: string;
  productName: string;
  description: string;
  quantity: number;
  unit: string;
  location: string;
  category: string;
  minStockLevel: number;
  total_quantity_left: number;
  category_name: string;
  product_name: string;
  qty: number;
  product_id: string;
}

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  border: "1px solid rgba(0,0,0,0.06)",
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
  },
}))

const HeaderCard = styled(Box)(({ theme }) => ({
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  padding: theme.spacing(3),
  borderRadius: "12px 12px 0 0",
}))

// New colorful gradient cards for sales summary
const SalesCard1 = styled(Card)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  borderRadius: 12,
  padding: theme.spacing(3),
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: -50,
    right: -50,
    width: 100,
    height: 100,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.1)",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -80,
    right: -30,
    width: 150,
    height: 150,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.05)",
  },
}))

const SalesCard2 = styled(Card)(({ theme }) => ({
  background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
  color: "white",
  borderRadius: 12,
  padding: theme.spacing(3),
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: -50,
    right: -50,
    width: 100,
    height: 100,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.1)",
  },
}))

const SalesCard3 = styled(Card)(({ theme }) => ({
  background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
  color: "white",
  borderRadius: 12,
  padding: theme.spacing(3),
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: -80,
    right: -30,
    width: 150,
    height: 150,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.05)",
  },
}))

const SalesCard4 = styled(Card)(({ theme }) => ({
  background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
  color: "white",
  borderRadius: 12,
  padding: theme.spacing(3),
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: -50,
    right: -50,
    width: 100,
    height: 100,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.1)",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -80,
    right: -30,
    width: 150,
    height: 150,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.05)",
  },
}))

// Inventory Cards
const InventoryCard = styled(Card)(({ theme }) => ({
  background: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
  color: "white",
  borderRadius: 12,
  padding: theme.spacing(3),
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: -50,
    right: -50,
    width: 100,
    height: 100,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.1)",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -80,
    right: -30,
    width: 150,
    height: 150,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.05)",
  },
}))

const InventoryCard2 = styled(Card)(({ theme }) => ({
  background: "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)",
  color: "white",
  borderRadius: 12,
  padding: theme.spacing(3),
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: -50,
    right: -50,
    width: 100,
    height: 100,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.1)",
  },
}))

const InventoryCard3 = styled(Card)(({ theme }) => ({
  background: "linear-gradient(135deg, #fad0c4 0%, #ffd1ff 100%)",
  color: "white",
  borderRadius: 12,
  padding: theme.spacing(3),
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: -80,
    right: -30,
    width: 150,
    height: 150,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.05)",
  },
}))

interface Product {
  id: string
  product_id: string
  name: string
  qty: number
}

const StockSalesReportPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const [stockCount, setStockCount] = useState(0)
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null)
  const [startDate, setStartDate] = useState<string | null>(null)
  const [endDate, setEndDate] = useState<string | null>(null)
  const [reportData, setReportData] = useState<any[] | null>(null)
  const [reportData2, setReportData2] = useState<any[] | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [selectedOrderDetails, setSelectedOrderDetails] = useState<any | null>(null)
  const [branches, setBranches] = useState<any[]>([])
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null)

  useEffect(() => {
    fetchBranches()
  }, [])

  useIonViewWillEnter(() => {
    fetchBranches()
  })

  const fetchBranches = async () => {
    try {
      const response = await getTlocations_production_centre()
      console.log(response)
      setBranches(response.data.data || [])
    } catch (error) {
      console.log(error)
      setToastMessage("Failed to fetch branches")
      setShowToast(true)
    }
  }

  const fetchProducts = async (selectedBranch: string) => {
    if (!selectedBranch) return;
    console.log("Selected branch ID:", selectedBranch);
    setLoading(true)
    try {
      const response = selectedBranch === 'all' 
        ? await getBarInventoryByBusiness() 
        : await getProductsByBranch(selectedBranch);

      setProducts(response.data)
      setStockCount(response.totalStockCount)
    } catch (error) {
      console.log(error)
      setToastMessage("Failed to fetch products")
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const handleGenerateReport = async () => {
    if (!selectedProduct || !startDate || !endDate || !selectedBranch) {
      setToastMessage("Please select a branch, product, and a date range.")
      setShowToast(true)
      return
    }
    setLoading(true)
    setReportData(null)
    setReportData2(null)
    try {
      const response = await getStockSalesReport({
        product_id: selectedProduct,
        startDate,
        endDate,
        branch_id: selectedBranch,
      })
      console.log(response);

      setReportData(response.data)
      setReportData2(response.data2)
      if (response?.data?.length === 0) {
        setToastMessage("No sales data found for the selected criteria.")
        setShowToast(true)
      }
    } catch (error) {
      console.error("Error fetching sales report:", error)
      setToastMessage("Failed to fetch sales report.")
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const [stockItems, setStockItems] = useState<StockItem[]>([])
  const [vendors, setVendors] = useState([])
  const [loadingData, setLoadingData] = useState(false)   
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  })
  
  const fetchData = async () => {
    try {
      const [response1, response2, response3] = await Promise.all([getProducts(), getVendors(), getStocksTransfer()])
      console.log([response1, response2, response3])
      setStockItems(response1.data.data || [])
      setVendors(response2.data.data || [])
    } catch (error) {
      console.error(error)
      setToast({
        open: true,
        message: "Failed to fetch data",
        severity: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const [branchProducts, setBranchProducts] = useState<StockItem[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const handleViewDetails = async (order_id: string) => {
    setDetailsOpen(true)
    setDetailsLoading(true)
    try {
      const response = await getStockSaleDetails({
        order_id,
        product_id: selectedProduct!,
        startDate: startDate!,
        endDate: endDate!,
      })
      console.log(response);
      setSelectedOrderDetails(response)
    } catch (error) {
      console.error("Error fetching order details:", error)
      setToastMessage("Failed to fetch order details.")
      setShowToast(true)
    } finally {
      setDetailsLoading(false)
    }
  }

  // Calculate totals for the summary cards
  const calculateTotals = () => {
    if (!reportData) return { totalQuantity: 0, totalRevenue: 0, totalCost: 0, totalProfit: 0 };
    
    const totalQuantity = reportData.reduce((acc, item) => acc + item.total_quantity, 0);
    const totalRevenue = reportData.reduce((acc, item) => acc + parseFloat(item.total_amount), 0);
    const totalCost = reportData.reduce((acc, item) => acc + parseFloat(item.total_cost_price), 0);
    const totalProfit = totalRevenue - totalCost;
    
    return { totalQuantity, totalRevenue, totalCost, totalProfit };
  };

  const totals = calculateTotals();

  return (
    <IonPage className="pushDown">
      <IonHeader className="ion-no-border ">
        <IonToolbar>
          <IonTitle> Stock Sales Report</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <Container maxWidth="lg" sx={{ py: 2 }}>
          <StyledCard sx={{ mb: 3 }}>
            <HeaderCard>
              <Typography
                variant="h6"
                fontWeight="600"
                sx={{ display: "flex", alignItems: "center", gap: 1, color: "#1e293b" }}
              >
                Generate Stock Sales Report
              </Typography>
            </HeaderCard>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Select Branch</InputLabel>
                    <Select
                      value={selectedBranch || ""}
                      onChange={(e) => {setSelectedBranch(e.target.value), fetchProducts(e.target.value)}}
                      label="Select Branch"
                    >
                      <MenuItem key={2} value={'all'}>
                        {'All Branches'}
                      </MenuItem>
                      {branches.map((branch) => (
                        <MenuItem key={branch.branch_id} value={branch.branch_id}>
                          {branch.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    value={products?.find((p) => p.product_id === selectedProduct) || null}
                    onChange={(event, newValue) => setSelectedProduct(newValue?.product_id || "")}
                    options={products || []}
                    getOptionLabel={(option) => option.name || ""}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Product" variant="outlined" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={startDate || ""}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={endDate || ""}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleGenerateReport}
                    disabled={!selectedProduct || !startDate || !endDate || !selectedBranch || loading}
                    sx={{
                      py: 1.5,
                      bgcolor: "#1e293b",
                      "&:hover": { bgcolor: "#334155" },
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Generate"}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>

          {reportData && (
            <StyledCard>
              <HeaderCard>
                <Typography variant="h6" fontWeight="600" color="#1e293b">
                  Report Results
                </Typography>
              </HeaderCard>
              <CardContent>
                {/* Sales Summary Cards */}
                <Typography variant="h6" sx={{ mb: 2, color: "#1e293b" }}>
                  Sales Summary
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={3}>
                    <SalesCard1>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LocalOffer sx={{ mr: 1, fontSize: 28 }} />
                        <Typography variant="h6" fontWeight="600">
                          Total Quantity Sold
                        </Typography>
                      </Box>
                      <Typography variant="h4" fontWeight="700" sx={{ mt: 1 }}>
                        {totals.totalQuantity.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                        Units sold in period
                      </Typography>
                    </SalesCard1>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <SalesCard2>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AttachMoney sx={{ mr: 1, fontSize: 28 }} />
                        <Typography variant="h6" fontWeight="600">
                          Total Revenue
                        </Typography>
                      </Box>
                      <Typography variant="h4" fontWeight="700" sx={{ mt: 1 }}>
                        {`₦${totals.totalRevenue.toLocaleString()}`}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                        Total sales revenue
                      </Typography>
                    </SalesCard2>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <SalesCard3>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <MonetizationOn sx={{ mr: 1, fontSize: 28 }} />
                        <Typography variant="h6" fontWeight="600">
                          Total Cost Price
                        </Typography>
                      </Box>
                      <Typography variant="h4" fontWeight="700" sx={{ mt: 1 }}>
                        {`₦${totals.totalCost.toLocaleString()}`}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                        Total cost of goods
                      </Typography>
                    </SalesCard3>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <SalesCard4>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <ShowChart sx={{ mr: 1, fontSize: 28 }} />
                        <Typography variant="h6" fontWeight="600">
                          Total Profit
                        </Typography>
                      </Box>
                      <Typography 
                        variant="h4" 
                        fontWeight="700" 
                        sx={{ mt: 1 }}
                      >
                        {`₦${totals.totalProfit.toLocaleString()}`}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                        Net profit margin
                      </Typography>
                    </SalesCard4>
                  </Grid>
                </Grid>

                {/* Inventory Summary Cards */}
                {reportData2 && reportData2.length > 0 && (
                <>
  <Typography variant="h6" sx={{ mb: 2, color: "#1e293b" }}>
    Inventory Summary
  </Typography>

  <Grid container spacing={3} sx={{ mb: 3 }}>
    {/* Opening Balance */}
    <Grid item xs={12} md={4}>
      <InventoryCard>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Balance sx={{ mr: 1, fontSize: 28 }} />
          <Typography variant="h6" fontWeight="600">
            Opening Balance
          </Typography>
        </Box>
        <Typography variant="h4" fontWeight="700" sx={{ mt: 1 }}>
          {Number(reportData2[0].total_consumed) + Number(reportData2[0].qty)}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
          Initial stock at period start
        </Typography>
      </InventoryCard>
    </Grid>

    {/* Total Consumed */}
    <Grid item xs={12} md={4}>
      <InventoryCard2>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <TrendingDown sx={{ mr: 1, fontSize: 28 }} />
          <Typography variant="h6" fontWeight="600">
            Total Consumed
          </Typography>
        </Box>
        <Typography variant="h4" fontWeight="700" sx={{ mt: 1 }}>
          {Number(reportData2[0].total_consumed)}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
          Units sold/used during period
        </Typography>
      </InventoryCard2>
    </Grid>

    {/* Closing Balance */}
    <Grid item xs={12} md={4}>
      <InventoryCard3>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <TrendingUp sx={{ mr: 1, fontSize: 28 }} />
          <Typography variant="h6" fontWeight="600">
            Closing Balance
          </Typography>
        </Box>
        <Typography variant="h4" fontWeight="700" sx={{ mt: 1 }}>
          {Number(reportData2[0].qty) }
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
          Remaining stock at period end
        </Typography>
      </InventoryCard3>
    </Grid>
  </Grid>
</>

                )}
              </CardContent>
            </StyledCard>
          )}
        </Container>

        <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #e2e8f0" }}>
            <Receipt sx={{ color: "#64748b" }} />
            Order Details
          </DialogTitle>
          <DialogContent sx={{ bgcolor: "#f8fafc", p: 3 }}>
            {detailsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
                <CircularProgress />
              </Box>
            ) : selectedOrderDetails ? (
              <Grid container spacing={3}>
                {/* Left Column */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#1e293b" }}>
                    Order Summary
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Event fontSize="small" sx={{ color: "#64748b" }} />
                      <Typography variant="body2" color="text.secondary">
                        Date:{" "}
                        <Typography component="span" fontWeight="500" color="text.primary">
                          {new Date(selectedOrderDetails.created_at).toLocaleString()}
                        </Typography>
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Storefront fontSize="small" sx={{ color: "#64748b" }} />
                      <Typography variant="body2" color="text.secondary">
                        Branch ID:{" "}
                        <Typography component="span" fontWeight="500" color="text.primary">
                          {selectedOrderDetails.branch_id}
                        </Typography>
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        icon={selectedOrderDetails.status === "COMPLETED" ? <CheckCircle /> : <Pending />}
                        label={selectedOrderDetails.status}
                        color={selectedOrderDetails.status === "COMPLETED" ? "success" : "warning"}
                        size="small"
                      />
                    </Box>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#1e293b" }}>
                    Customer & Staff
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Person fontSize="small" sx={{ color: "#64748b" }} />
                      <Typography variant="body2" color="text.secondary">
                        Customer:{" "}
                        <Typography component="span" fontWeight="500" color="text.primary">
                          {selectedOrderDetails.customer_name}
                        </Typography>
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Phone fontSize="small" sx={{ color: "#64748b" }} />
                      <Typography variant="body2" color="text.secondary">
                        Phone:{" "}
                        <Typography component="span" fontWeight="500" color="text.primary">
                          {selectedOrderDetails.customer_phone}
                        </Typography>
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Badge fontSize="small" sx={{ color: "#64748b" }} />
                      <Typography variant="body2" color="text.secondary">
                        Served By:{" "}
                        <Typography component="span" fontWeight="500" color="text.primary">
                          {selectedOrderDetails.staff_name}
                        </Typography>
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>

                {/* Right Column */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#1e293b" }}>
                    Product Details
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="body1" fontWeight="600" color="primary.main">
                      {products.find((p) => p.product_id === selectedOrderDetails.product_id)?.name ||
                        selectedOrderDetails.product_id}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Quantity:
                        </Typography>
                        <Typography fontWeight="500">{selectedOrderDetails.quantity}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Unit Price:
                        </Typography>
                        <Typography fontWeight="500">{`₦${parseFloat(
                          selectedOrderDetails.price
                        ).toLocaleString()}`}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Cost Price:
                        </Typography>
                        <Typography fontWeight="500">{`₦${parseFloat(
                          selectedOrderDetails.purchasePrice
                        ).toLocaleString()}`}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Unit Profit:
                        </Typography>
                        <Typography 
                          fontWeight="500"
                          sx={{ 
                            color: (parseFloat(selectedOrderDetails.price) - parseFloat(selectedOrderDetails.purchasePrice)) >= 0 
                              ? 'success.main' 
                              : 'error.main'
                          }}
                        >
                          {`₦${(parseFloat(selectedOrderDetails.price) - parseFloat(selectedOrderDetails.purchasePrice)).toLocaleString()}`}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Total Revenue:
                        </Typography>
                        <Typography variant="h6" fontWeight="600" color="primary.main">
                          {`₦${parseFloat(selectedOrderDetails.total_amount).toLocaleString()}`}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Total Cost:
                        </Typography>
                        <Typography variant="h6" fontWeight="600">
                          {`₦${parseFloat(selectedOrderDetails.total_cost_price).toLocaleString()}`}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Total Profit:
                        </Typography>
                        <Typography 
                          variant="h5" 
                          fontWeight="600" 
                          sx={{ 
                            color: (parseFloat(selectedOrderDetails.total_amount) - parseFloat(selectedOrderDetails.total_cost_price)) >= 0 
                              ? 'success.main' 
                              : 'error.main'
                          }}
                        >
                          {`₦${(parseFloat(selectedOrderDetails.total_amount) - parseFloat(selectedOrderDetails.total_cost_price)).toLocaleString()}`}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            ) : (
              <Typography sx={{ p: 4, textAlign: "center" }}>No details found.</Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: "1px solid #e2e8f0" }}>
            <Button onClick={() => setDetailsOpen(false)} variant="outlined">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <IonLoading isOpen={loading} message="Processing..." />
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          position="top"
        />
      </IonContent>
    </IonPage>
  )
}

export default StockSalesReportPage